import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, mergeMap } from 'rxjs/operators';
import { AuthService } from '../service/auth.service';
import * as jwt_decode from 'jwt-decode';
import { ConfigService } from '../service/config.service';
import { EtTokenService } from '../shared/services/customer-api/et-token.service';
import { TokenService } from '../shared/services/customer-api/token.service';

/**
 * This guard check if you are registered or if you was logged and your token is not expired
 */
export const authGuard: CanActivateFn = (next, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const config = inject(ConfigService);
  const etToken = inject(EtTokenService);
  const tokenService = inject(TokenService);

  const runStandardChecks = (): Observable<boolean> | boolean => {
    const params = state.root.queryParams;
    const token = state.root.queryParams.jwt;
    const redirectURL = decodeURIComponent(state.root.queryParams.redirectURL);
    if (params.impersonate) {
      let email = 'true';
      if (token) {
        const user = jwt_decode(token);
        email = user.email;
      } else if (auth.userdata) {
        email = auth.userdata.email;
      }
      if (config.appConfig.setup.useStorage) {
        localStorage.setItem('impersonate', email);
      }
      // Mark this tab per-tab so the impersonated token persists to
      // sessionStorage (not the shared localStorage `token` key), which is what
      // caused the cross-tab logout every refresh cycle.
      tokenService.setImpersonating(true);
      router.navigate([state.url.split('?')[0]], {
        queryParams: Object.assign({}, state.root.queryParams, {
          impersonate: null,
        }),
        queryParamsHandling: 'merge',
      });
      return false;
    } else if (token || auth.jwtAuthorized === true) {
      if (auth.jwtAuthorized === true) {
        return true;
      } else {
        return auth.jwtAuth(token).pipe(
          mergeMap(() => auth.loadFeatureFlags(token)),
          map(() => true),
          catchError(() => {
            if (redirectURL) {
              window.location.href = redirectURL;
            } else {
              router.navigate(['/message/autherror']);
            }
            return of(false);
          }),
        );
      }
    } else {
      return auth.authLoged().pipe(
        mergeMap(() => {
          let mytoken;
          if (config.appConfig.setup.useStorage) {
            mytoken = localStorage.getItem('token');
          }
          if (mytoken) {
            return auth.loadFeatureFlags(mytoken);
          } else {
            return of(false);
          }
        }),
        map(() => {
          const user = auth.authToken();
          if (user) {
            const expiry = new Date(user.exp * 1000);
            if (expiry < new Date()) {
              router.navigate(['/login'], {
                queryParams: { url: state.url },
              });
              return false;
            } else {
              return true;
            }
          } else {
            router.navigate(['/login'], {
              queryParams: { url: state.url },
            });
            return false;
          }
        }),
        catchError((err) => {
          if (err === 'Unauthorized') {
            router.navigate(['/login'], {
              queryParams: { url: state.url },
            });
            return of(false);
          } else {
            return of(true);
          }
        }),
      );
    }
  };

  return etToken.tryAutoLogin().pipe(
    mergeMap(() => {
      const result = runStandardChecks();
      return typeof result === 'boolean' ? of(result) : result;
    }),
  );
};
